// Highlights: overzicht van guides + lijst-detail met mini-map.

import { setTitle } from '../main.js'
import { md } from '../markdown.js'
import { icon, hasIcon } from '../icons.js'
import { getStore } from '../store.js'
import { fetchImage } from '../images.js'
import { esc } from '../util.js'

function guideIcon(g) {
  if (g.icon && hasIcon(g.icon)) return icon(g.icon)
  const map = {
    kastelen: 'castle', steden: 'building-2', natuur: 'trees',
    eten: 'beer', bijzonder: 'sparkles',
  }
  return icon(map[g.id] ?? 'compass')
}

// ---------- Mini-map ----------
// SVG-kaartje met OSM-tegel achtergrond en de coordinaten van de lijst-entries,
// plus optioneel de thuisbasis als refererend punt.
function renderMiniMap(coords, homeBase) {
  const points = coords.filter(c => Number.isFinite(c?.lat) && Number.isFinite(c?.lng))
  if (!points.length) return ''

  const all = [...points]
  if (homeBase && Number.isFinite(homeBase.lat) && Number.isFinite(homeBase.lng)) all.push(homeBase)

  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity
  for (const p of all) {
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
    if (p.lng < minLng) minLng = p.lng
    if (p.lng > maxLng) maxLng = p.lng
  }
  const padLat = Math.max(0.04, (maxLat - minLat) * 0.2)
  const padLng = Math.max(0.04, (maxLng - minLng) * 0.2)
  minLat -= padLat; maxLat += padLat; minLng -= padLng; maxLng += padLng

  const W = 320, H = 180
  const project = (lat, lng) => {
    const x = ((lng - minLng) / (maxLng - minLng || 1)) * W
    const y = H - ((lat - minLat) / (maxLat - minLat || 1)) * H
    return [x, y]
  }

  // OSM-tegels als subtiele achtergrond
  const zoom = 9
  const n = Math.pow(2, zoom)
  const toTileX = lng => Math.floor(n * (lng + 180) / 360)
  const toTileY = lat => {
    const r = lat * Math.PI / 180
    return Math.floor(n * (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2)
  }
  const tileLat = (ty) => {
    const r = Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / n)))
    return r * 180 / Math.PI
  }
  const tileLng = (tx) => tx / n * 360 - 180

  const tx0 = toTileX(minLng), tx1 = toTileX(maxLng)
  const ty0 = toTileY(maxLat), ty1 = toTileY(minLat)
  let tiles = ''
  for (let tx = tx0; tx <= tx1; tx++) {
    for (let ty = ty0; ty <= ty1; ty++) {
      const [sx0, sy0] = project(tileLat(ty), tileLng(tx))
      const [sx1, sy1] = project(tileLat(ty + 1), tileLng(tx + 1))
      tiles += `<image href="https://a.tile.openstreetmap.org/${zoom}/${tx}/${ty}.png" x="${sx0.toFixed(1)}" y="${sy0.toFixed(1)}" width="${(sx1 - sx0).toFixed(1)}" height="${(sy1 - sy0).toFixed(1)}" preserveAspectRatio="none"/>`
    }
  }

  const dots = points.map((p, i) => {
    const [x, y] = project(p.lat, p.lng)
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6" fill="#11457E" stroke="#fff" stroke-width="2"/>
            <text x="${x.toFixed(1)}" y="${(y + 3.5).toFixed(1)}" font-size="8" font-weight="700" text-anchor="middle" fill="#fff" font-family="Geist, sans-serif">${i + 1}</text>`
  }).join('')

  const home = homeBase ? (() => {
    const [x, y] = project(homeBase.lat, homeBase.lng)
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="5" fill="#D7141A" stroke="#fff" stroke-width="2"/>
            <text x="${x.toFixed(1)}" y="${(y - 10).toFixed(1)}" font-size="10" text-anchor="middle" fill="#11457E" font-family="Geist, sans-serif" font-weight="700">⌂</text>`
  })() : ''

  return `
    <div class="mini-map">
      <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <rect width="${W}" height="${H}" fill="#E2EAF5"/>
        ${tiles}
        <rect width="${W}" height="${H}" fill="rgba(255,255,255,.25)"/>
        ${home}
        ${dots}
      </svg>
      <div class="mini-map-attr">© OpenStreetMap</div>
    </div>`
}

// ---------- Overzicht ----------
export function renderHighlights(root, ctx) {
  setTitle('')
  const guides = ctx.data.guides ?? []
  if (!guides.length) {
    root.innerHTML = `<div class="page"><h1 class="title">Highlights</h1><p class="empty">Nog geen lijstjes.</p></div>`
    return
  }
  const cards = guides.map(g => `
    <a class="entity" href="#/highlights/${g.id}">
      <div class="icon-bubble">${guideIcon(g)}</div>
      <div class="e-body">
        <div class="e-title">${esc(g.title)}</div>
        ${g.intro ? `<div class="e-note">${esc(g.intro)}</div>` : ''}
        <div class="e-meta">${g.entries?.length ?? 0} plekken</div>
      </div>
    </a>`).join('')

  root.innerHTML = `
    <div class="page">
      <h1 class="title">Highlights</h1>
      <p class="subtitle">Handgekozen selecties</p>
      <div class="entity-list">${cards}</div>
    </div>
  `
}

// ---------- Detail ----------
export function renderHighlight(root, ctx, id) {
  const guide = (ctx.data.guides ?? []).find(g => g.id === id)
  if (!guide) {
    setTitle('')
    root.innerHTML = `<div class="page"><p class="empty">Onbekend: ${esc(id)}</p></div>`
    return
  }
  setTitle(guide.title)

  const enriched = (guide.entries ?? []).map((entry, idx) => {
    const n = ctx.indexes.nodesById.get(entry.nodeId)
    return n ? { entry, n, idx } : null
  }).filter(Boolean)

  const TOP_N = 8
  const hasMore = enriched.length > TOP_N
  const topItems = hasMore ? enriched.slice(0, TOP_N) : enriched

  const homeBase = ctx.data.meta?.homeBaseCoord ?? null
  const miniMap = renderMiniMap(topItems.map(e => e.n.coord), homeBase)

  function renderItem({ entry, n, idx }) {
    const cat = ctx.indexes.categoryById.get(n.category)
    const catIcon = cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('sparkles')
    return `
      <a class="entity cat-${n.category || 'gebouw'}" data-node-id="${esc(n.id)}" href="#/node/${n.id}">
        <div class="icon-bubble entity-thumb" data-thumb="${esc(n.id)}">${catIcon}</div>
        <div class="e-body">
          <div class="e-title">${idx + 1}. ${esc(n.name)}</div>
          ${entry.note ? `<div class="e-note">${esc(entry.note)}</div>` : ''}
          ${cat ? `<div class="e-meta">${esc(cat.label)}${typeof n.driveTimeMin === 'number' ? ` · ${n.driveTimeMin} min rijden` : ''}</div>` : ''}
        </div>
      </a>`
  }

  const topHtml = topItems.map(renderItem).join('')
  const restHtml = hasMore ? enriched.slice(TOP_N).map(renderItem).join('') : ''

  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/highlights">‹ Highlights</a></div>
      <h1 class="title">${esc(guide.title)}</h1>
      ${guide.intro ? `<div class="blurb">${md(guide.intro)}</div>` : ''}
      ${miniMap}
      <div class="entity-list">${topHtml || '<p class="empty">Lege lijst.</p>'}</div>
      ${hasMore ? `
        <div class="entity-list more-items" hidden>${restHtml}</div>
        <button class="btn secondary" id="show-more-highlights" style="margin-top:12px">
          ${icon('chevron-down')} Toon alle ${enriched.length} plekken
        </button>
      ` : ''}
    </div>
  `

  if (hasMore) {
    root.querySelector('#show-more-highlights')?.addEventListener('click', (e) => {
      const btn = e.currentTarget
      const moreList = root.querySelector('.more-items')
      moreList.hidden = !moreList.hidden
      btn.innerHTML = moreList.hidden
        ? `${icon('chevron-down')} Toon alle ${enriched.length} plekken`
        : `${icon('minus')} Toon minder`
      // Load thumbnails for newly visible items
      if (!moreList.hidden) loadThumbnails(enriched.slice(TOP_N), root, ctx)
    })
  }

  loadThumbnails(topItems, root, ctx)
}

function loadThumbnails(items, root, ctx) {
  items.forEach(({ n }) => {
    fetchImage('node', n, ctx).then(url => {
      if (!url) return
      const slot = root.querySelector(`.entity-thumb[data-thumb="${CSS.escape(n.id)}"]`)
      if (slot) slot.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy"/>`
    })
  })
}
